import { useState, useEffect, useRef } from "react";
import { Link, useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import logoSrc from "@/assets/logo.png";

interface DropdownItem {
  label: string;
  path: string;
  icon: string;
  desc: string;
}

interface NavItem {
  label: string;
  path?: string;
  dropdown?: DropdownItem[];
}

const navItems: NavItem[] = [
  {
    label: "Product",
    dropdown: [
      { label: "Features Overview", path: "/features", icon: "ri-layout-grid-line", desc: "Everything ClickSendAI can do" },
      { label: "AI Content Creation", path: "/product/ai-content", icon: "ri-robot-2-line", desc: "GPT-4 powered post generation" },
      { label: "Multi-Network Posting", path: "/product/multi-network", icon: "ri-share-forward-2-line", desc: "50+ platforms, one dashboard" },
      { label: "Reddit & Quora AI Agent", path: "/product/forum-agent", icon: "ri-reddit-fill", desc: "Auto-reply & drive traffic 24/7" },
      { label: "Landing Pages", path: "/features/landing-pages", icon: "ri-pages-fill", desc: "100+ prebuilt niche templates" },
      { label: "Smart Scheduling", path: "/product/scheduling", icon: "ri-calendar-2-line", desc: "Post at peak engagement times" },
      { label: "Analytics & Reporting", path: "/product/analytics", icon: "ri-bar-chart-2-line", desc: "Track what drives members" },
      { label: "Affiliate Marketing Suite", path: "/affiliate-tools", icon: "ri-links-line", desc: "URL-to-content AI generation" },
    ],
  },
  {
    label: "Use Cases",
    dropdown: [
      { label: "Skool Group Owners", path: "/solutions/skool-groups", icon: "ri-group-line", desc: "Grow your community externally" },
      { label: "Course Creators", path: "/solutions/course-creators", icon: "ri-book-open-line", desc: "Promote your courses everywhere" },
      { label: "Coaches & Consultants", path: "/solutions/coaches", icon: "ri-user-star-line", desc: "Build authority across networks" },
      { label: "Marketing Agencies", path: "/solutions/marketing-agencies", icon: "ri-building-line", desc: "Manage multiple clients" },
      { label: "Affiliate Marketers", path: "/solutions/affiliate-marketers", icon: "ri-percent-line", desc: "Auto-post with your links" },
    ],
  },
  { label: "Pricing", path: "/get-started" },
  {
    label: "Resources",
    dropdown: [
      { label: "Affiliate Tools", path: "/affiliate-tools", icon: "ri-links-line", desc: "URL-to-content AI generation" },
      { label: "Help Center", path: "/contact", icon: "ri-question-line", desc: "Guides and documentation" },
      { label: "Contact Us", path: "/contact", icon: "ri-mail-line", desc: "Talk to our team" },
      { label: "Privacy Policy", path: "/privacy", icon: "ri-shield-check-line", desc: "How we protect your data" },
      { label: "Terms of Service", path: "/terms", icon: "ri-file-text-line", desc: "Platform terms and conditions" },
    ],
  },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [mobileExpanded, setMobileExpanded] = useState<string | null>(null);
  const location = useLocation();
  const { user, signOut } = useAuth();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setOpenDropdown(null);
  }, [location.pathname]);

  const isActive = (path?: string) => path && location.pathname === path;

  const textColor = "text-white hover:text-white/80";

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-[#0f0a2e]/95 backdrop-blur-md border-b border-white/10 shadow-lg"
          : "bg-gradient-to-b from-black/55 to-transparent"
      }`}
    >
      <div className="px-4 md:px-8">
        <div className="flex items-center h-16 md:h-20 gap-6">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 cursor-pointer shrink-0">
            <img
              src={logoSrc}
              alt="ClickSendAI Logo"
              className="h-12 md:h-14 w-auto object-contain"
            />
          </Link>

          {/* Desktop Nav — centered */}
          <div ref={dropdownRef} className="hidden md:flex items-center justify-center flex-1 gap-1">
            {navItems.map((item) => (
              <div key={item.label} className="relative">
                {item.path ? (
                  <Link
                    to={item.path}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      isActive(item.path) ? "text-violet-300 font-semibold" : textColor
                    }`}
                  >
                    {item.label}
                  </Link>
                ) : (
                  <button
                    onClick={() => setOpenDropdown(openDropdown === item.label ? null : item.label)}
                    className={`flex items-center gap-1 px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${textColor}`}
                  >
                    {item.label}
                    <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${openDropdown === item.label ? "rotate-180" : ""}`}></i>
                  </button>
                )}

                {/* Dropdown */}
                {item.dropdown && openDropdown === item.label && (
                  <div className="absolute top-full left-1/2 -translate-x-1/2 mt-2 w-72 bg-white rounded-2xl border border-gray-100 overflow-hidden z-50 animate-in fade-in slide-in-from-top-2 duration-150">
                    <div className="p-2">
                      {item.dropdown.map((d) => (
                        <Link
                          key={d.label}
                          to={d.path}
                          onClick={() => setOpenDropdown(null)}
                          className="flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-violet-50 transition-colors cursor-pointer group"
                        >
                          <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-violet-100 group-hover:bg-violet-200 transition-colors shrink-0 mt-0.5">
                            <i className={`${d.icon} text-violet-600 text-sm`}></i>
                          </div>
                          <div>
                            <div className="text-sm font-semibold text-gray-900 group-hover:text-violet-700 transition-colors">{d.label}</div>
                            <div className="text-xs text-gray-400 mt-0.5">{d.desc}</div>
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* CTA Buttons */}
          <div className="hidden md:flex items-center gap-3 shrink-0">
            {user ? (
              <>
                <Link
                  to="/dashboard"
                  className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${textColor}`}
                >
                  Dashboard
                </Link>
                <button
                  onClick={() => signOut()}
                  className="text-sm font-semibold px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
                >
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/login"
                  className={`text-sm font-medium px-4 py-2 rounded-lg transition-colors whitespace-nowrap cursor-pointer ${textColor}`}
                >
                  Log In
                </Link>
                <Link
                  to="/get-started"
                  className="text-sm font-semibold px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white hover:opacity-90 transition-opacity whitespace-nowrap cursor-pointer"
                >
                  Start Free Trial
                </Link>
              </>
            )}
          </div>

          {/* Mobile Hamburger */}
          <button
            className="md:hidden ml-auto w-10 h-10 flex items-center justify-center cursor-pointer"
            onClick={() => setMobileOpen(!mobileOpen)}
          >
            <i className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-2xl text-white`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 px-4 py-4 flex flex-col gap-1 max-h-[80vh] overflow-y-auto">
          {navItems.map((item) => (
            <div key={item.label}>
              {item.path ? (
                <Link
                  to={item.path}
                  onClick={() => setMobileOpen(false)}
                  className={`block text-sm font-medium py-2.5 px-3 rounded-lg cursor-pointer ${
                    isActive(item.path) ? "text-violet-600 bg-violet-50" : "text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  {item.label}
                </Link>
              ) : (
                <>
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === item.label ? null : item.label)}
                    className="w-full flex items-center justify-between text-sm font-medium py-2.5 px-3 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer"
                  >
                    {item.label}
                    <i className={`ri-arrow-down-s-line transition-transform ${mobileExpanded === item.label ? "rotate-180" : ""}`}></i>
                  </button>
                  {mobileExpanded === item.label && item.dropdown && (
                    <div className="ml-3 mt-1 space-y-1 border-l-2 border-violet-100 pl-3">
                      {item.dropdown.map((d) => (
                        <Link
                          key={d.label}
                          to={d.path}
                          onClick={() => setMobileOpen(false)}
                          className="flex items-center gap-2 py-2 text-sm text-gray-600 hover:text-violet-600 cursor-pointer"
                        >
                          <i className={`${d.icon} text-violet-400 text-sm`}></i>
                          {d.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}

          <div className="pt-3 mt-2 border-t border-gray-100 flex flex-col gap-2">
            {user ? (
              <>
                <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-2.5 px-3 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Dashboard
                </Link>
                <button onClick={() => { signOut(); setMobileOpen(false); }} className="text-center text-sm font-semibold px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white cursor-pointer">
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileOpen(false)} className="text-sm font-medium py-2.5 px-3 rounded-lg text-gray-700 hover:bg-gray-50 cursor-pointer">
                  Log In
                </Link>
                <Link to="/get-started" onClick={() => setMobileOpen(false)} className="text-center text-sm font-semibold px-5 py-2.5 rounded-full bg-gradient-to-r from-violet-600 to-fuchsia-500 text-white cursor-pointer">
                  Start Free Trial
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
